import type { PropertyValues, TemplateResult } from 'lit';
import { css, html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import _, { isEqual } from 'lodash';
import type * as Y from 'yjs';
import type { ViewerPropertiesConfig } from 'src/services/viewer-properties-types';
import { viewerPropertiesManager } from 'src/services/viewer-properties-manager';
import { levelContentStyles } from 'src/library/level-content-styles';
import { LocalDate } from 'src/utilities/local-date';
import { LocalDateTime } from 'src/utilities/local-date-time';
import { isEmptyHtmlContent, uuid } from 'src/utilities/text';
import '../library/elements/d-wrap.js';
import '../library/lists/d-list-section-item';
import { sorted } from '../store/utilities';

import '../library/components/d-field-section';
import '../library/elements/d-section';
import '../library/editors/components/d-edit-classification';
import '../library/editors/elements/d-select-tag';
import '../library/lists/d-list-section';
import './d-entity-diff';
import '../library/promo/d-promo-section';
import '../library/components/d-help-section';
import '../library/lists/d-list-section-attachment';
import '../library/fields/d-spinner';
import '../library/editors/elements/d-yjs-textarea';
import '../library/elements/d-smooth-resize.js';

import './d-entity-header';
import './d-update-section';
import 'src/library/elements/d-action';
import { features } from '../store/selectors/features/features';
import { AddToCollectionDialog } from 'src/content/add-to-collection-dialog';
import { shortenPath } from 'src/utilities/path';
import {
  getPdfTemplateDefinitionForEntity,
  getValueForPdfTemplate,
  getViewFieldsForPdfTemplate,
} from 'src/store/selectors/pdf-template-functions';
import type { UpdateSectionItem } from './update-section-item';
import type { AddToCollectionDialogResult } from './add-to-collection-dialog-result';
import type { ListSectionItemInput } from '../library/lists/listSectionItemInput';
import type { Revision } from './revision';
import type { AbstractEntityView } from './abstract-entity-view';
import type { AbstractEntityViewWithRevisions } from './abstract-entity-view-with-revisions';
import type { ListDefinition } from './list-definition';
import type { SectionField } from '../library/components/section-field';
import type { ActionInput } from '../library/elements/action-input';
import type { EntityOption } from 'src/layout/application-view-model';
import type { ItemPdf } from '../pdf/item-pdf';
import type { PdfField } from '../pdf/types';
import type { SystemField } from '../pdf/system-types';
import { mapFieldsToPdfContent } from '../pdf/map-fields-to-pdf-content';
import { ResponsiveElement } from 'src/library/elements/responsive-container';
import type { EventOccurrenceViewModel, TopicViewModel } from '@trinnvis/dabih-api-client';
import { hsePlanTemplateId } from 'src/store/selectors/templates';
import type { CollectionEditDoneDetail } from 'src/content/collection-edit-done-detail';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { getStore } from 'src/store';
import { createClient } from 'src/store/backend';
import { relatedEntitiesSectionStyles } from 'src/content/related-entities-section-styles';
import { descriptionLinksToEntity } from 'src/content/review-task-matching';
import { pageIds } from 'src/store/selectors/pages/pageIds';
import { eventOccurrencesNotDeleted } from 'src/store/selectors/events/event-occurrences-not-deleted';
import type { CreateEntityInput } from 'src/layout/parts/create-entity-input';
import type { CreateEventOccurrenceInput } from 'src/layout/parts/create-event-occurrence-input';
import type { ReminderType } from 'src/content/event-occurrences/reminder-type';
import { t } from 'src/i18n';
import { getOrganizationId } from 'src/store/selectors/organization/getOrganizationId';
import { TranslationDialog } from 'src/content/translation-dialog';
import { getTranslatableFields } from 'src/content/translatable-fields';
import type { TranslatableField } from 'src/services/translation-types';

export function toLookup(entries: { [p: string]: string }): Map<string, string> {
  return new Map<string, string>(Object.entries(entries));
}

/**
 * Escapes a string for safe interpolation into HTML text and attribute values.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 *
 * USAGE:
 *    d-meeting-event-view
 *    d-task-view
 *
 *
 */
export abstract class EntityContent<
  T extends AbstractEntityView<U> | AbstractEntityViewWithRevisions<U>,
  U,
> extends ResponsiveElement {
  static readonly styles = [
    levelContentStyles,
    relatedEntitiesSectionStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        position: relative;
        padding-bottom: 0;
        background: white;
        transition: padding-bottom 0.3s;
      }

      .meta-section {
        border: 1px solid var(--borderColor);
        border-bottom: none;
        background: hsl(0, 0%, 96%);
        padding: 14px 18px;
      }

      .related-task d-action {
        margin: -6px -4px;
      }

      .related-task + d-view-occurrences {
        margin-top: 6px;
      }

      .event-time {
        margin-top: 24px;
        margin-bottom: 16px;
        font-family: var(--mainSans);
        font-size: 25.6px;
        font-weight: 400;
      }

      d-section > d-action:first-child {
        margin-left: -6px;
      }

      .button {
        display: inline-block;
        padding: 8px 12px;
        background-color: var(--themeColor);
        color: white;
        font-family: var(--mainSans), sans-serif;
        border-radius: 6px;
        cursor: pointer;
      }

      .button:hover {
        background-color: var(--themeColorDarkerOne);
      }

      d-list-section:not(.related-assets) {
        margin-bottom: 6px;
      }

      .delayedFadeIn {
        animation-name: fadeIn;
        animation-duration: 400ms;
        animation-timing-function: ease;
        animation-iteration-count: 1;
        animation-delay: 600ms;
        animation-fill-mode: forwards;
      }

      @keyframes fadeIn {
        0% {
          opacity: 0;
        }
        100% {
          opacity: 1;
        }
      }

      .list-resize-wrapper {
        margin-right: -20px;
        margin-bottom: 1px;
        padding-right: 20px;
      }
    `,
  ];

  /**
   * The href of the current item displayed. Used as context for relative links
   */
  @property({ type: String })
  href = '';
  @property({ type: Boolean })
  singleUserVersion = false;
  @property({ type: String })
  parentId = '';
  @property({ type: String })
  parentEntityType = '';
  @property({ type: String })
  afterDeleteHref = '';
  @property({ type: Number })
  pageId = 0;
  @property({ type: String })
  editHref = '';
  @property({ type: String })
  icon = '';
  @property({ type: String })
  labelForTaskEvent = '';
  @property({ type: Boolean })
  updateSectionOpened = false;
  @property({ type: String })
  helpContentForEvent = '';
  @property({ type: String })
  helpContent = '';
  @property({ type: String })
  today = '';
  @property({ type: String })
  helpContentComputed = '';
  @property({ type: Boolean })
  hasHelp = false;
  @property({ type: Boolean })
  helpOpened = false;
  @property({ type: Number })
  templateId = 0;
  @property({ type: String })
  revisionsUrl = '';
  @property({ type: String })
  mode = 'normal';
  @property({ type: String })
  deleteWarning = t('content.deleteWarning');
  @property({ type: Boolean })
  noView = false;
  @property({ type: Boolean })
  itemLocked = false;
  @property({ type: Boolean })
  duplicatable = false;
  @property({ type: Boolean })
  eventDone = false;
  @property({ type: Array })
  activityCodes = [];
  @property({ type: String })
  token = '';
  @property({ type: Object })
  revision?: Revision;
  @property({ type: Object })
  task = {};
  @property({ type: String })
  key = '';
  @property({ type: String })
  currentEntityType?: string = '';
  @property({ type: Object })
  fieldsByCode = {};
  @property({ type: Boolean })
  isDoneDisabled = false;
  @property({ type: String })
  parentHref = '';
  @property({ type: Object })
  entityView!: T;
  @property({ type: String })
  organizationId = '';
  @property({ type: String })
  userId = '';
  @property({ type: Boolean })
  covered = false;
  @property({ type: Number })
  stickyTop = 0;
  @property({ type: Number })
  contentStickyTop = 0;
  @property({ type: Boolean, attribute: 'prevent-header-shrink' })
  preventHeaderShrink = false;
  @state()
  protected editItem?: U;
  protected previousEditItem?: {
    editItem: U;
    entityUuid: string;
  };

  @state()
  private savingDraft = false;

  // Don't use @state() for privateNotesText to avoid re-renders
  private privateNotesText?: Y.Text;

  @state()
  private privateNotesLoading = false;

  @state()
  private privateNotesInitialized = false;

  private viewerPropertiesCleanup?: () => void;
  private currentViewerPropertiesKey?: string;
  private initializationPromise?: Promise<void>;

  private debouncedDoBackup = _.debounce(() => this.onBackup(), 500, { maxWait: 5000 });

  get helpOnly() {
    return false;
  }

  protected viewerPropertiesConfig(): ViewerPropertiesConfig | undefined {
    return undefined;
  }

  protected get currentItemLabel() {
    return this.entityView.name ?? '';
  }

  protected get currentItemSecondaryLabel() {
    return '';
  }

  protected get currentItemSublabel() {
    return '';
  }

  protected get specialEditLabel() {
    return '';
  }

  protected get specialEditSublabel() {
    return '';
  }

  protected get preventDelete(): boolean {
    return false;
  }

  protected get internalMode() {
    if (this.mode === 'saving') return 'saving';
    return this.noView ? 'edit' : this.mode;
  }

  protected get hideEditHeader() {
    return false;
  }

  protected get editName() {
    if (this.editItem) {
      const editItem = this.editItem as { name?: string; firstName?: string; lastName?: string };
      if (editItem.firstName || editItem.lastName) {
        return editItem.firstName + ' ' + editItem.lastName;
      }
      if (editItem.name) {
        return editItem.name;
      }
    }
    return '';
  }

  private get hasEditAccess() {
    return this.entityView.currentUserHasWriteAccess && !this.entityView.deleted;
  }

  private get collectionUuid() {
    const pathArray = this.parentHref.split('/');
    if (pathArray[pathArray.length - 2] === 'collections') {
      return pathArray[pathArray.length - 1];
    }
    return undefined;
  }

  private get collectionsWithCurrentEntity() {
    if (this.entityView.availableCollections) {
      return this.entityView.availableCollections
        .filter((collection) => {
          const itemIds = collection.items.map((item) => {
            return item.uuid;
          });
          return itemIds.includes(this.entityView.uuid);
        })
        .map((collection) => {
          return {
            name: collection.name,
            href: shortenPath(
              this.parentHref +
                '/' +
                this.entityView.type +
                '/' +
                this.entityView.uuid +
                '/collections/' +
                collection.uuid,
            ),
            uuid: collection.uuid,
          };
        });
    }
    return [];
  }

  private get saveAsNewAction() {
    if (this.entityView.type === 'riskAssessments') {
      return {
        name: t('content.reassessIncident'),
        action: 'repeatRiskAssessment',
      };
    }
    return {
      name: t('content.createCopyOfDocument'),
      action: 'saveAsNew',
    };
  }

  _modeChange() {
    this.helpOpened = false;
  }

  _normalMode(mode) {
    return mode === 'normal';
  }

  _hideSuggestedRevision(mode) {
    return mode !== 'normal';
  }

  _helpSectionOpened(
    helpOnly: boolean,
    helpOpened: boolean,
    mode: string,
    templateId: number,
    unchangedTemplate: boolean | undefined,
    isConfirmedEntity: boolean,
  ) {
    if (unchangedTemplate || !isConfirmedEntity) {
      return true;
    }
    return mode === 'normal' && (helpOnly || helpOpened || templateId === 7896);
  }

  async onEditModeOn() {
    this.editItem = undefined;
    try {
      await this.doInitializeEditItem();
      const e = this.editItem;
      if (e !== undefined) {
        this.previousEditItem = {
          editItem: e,
          entityUuid: this.entityView.uuid,
        };
      }
      this.dispatchEvent(new CustomEvent('edit', { bubbles: true, composed: true }));
      this.mode = 'edit';
    } catch (error) {
      // Error already shown to user in doInitializeEditItem
      // Stay in normal mode - don't activate edit mode
      console.error('[onEditModeOn] Failed to activate edit mode:', error);
    }
  }

  onEditModeOff() {
    /**
     * Navigate to parent (close entity) if entity is
     * - draft-only,
     * - only meant for edit mode, or
     * - template-based, presented as suggestion and unchanged
     */
    if (!this.entityView.isConfirmedEntity || this.noView || this.entityView.unchangedTemplate) {
      this.dispatchEvent(
        new CustomEvent('navigate', {
          bubbles: true,
          composed: true,
          detail: { href: this.entityView.parentHref },
        }),
      );
    }
    this.mode = 'normal';
    this.dispatchEvent(new CustomEvent('edit-off', { bubbles: true, composed: true }));
    this.editItem = undefined;
  }

  _helpContent(helpContent, helpContentForTaskEvent) {
    if (helpContentForTaskEvent) {
      return helpContentForTaskEvent;
    }
    if (helpContent) {
      return helpContent;
    }
  }

  async _saveAsNew() {
    if (!this.isDoneDisabled) {
      if (this.editItem === undefined) {
        try {
          await this.doInitializeEditItem();
        } catch (error) {
          // Error already shown to user in doInitializeEditItem
          console.error('[_saveAsNew] Failed to initialize edit item:', error);
          return; // Don't proceed with save-as-new
        }
      }
      this.dispatchEvent(
        new CustomEvent('save-as-new', {
          bubbles: true,
          composed: true,
          detail: {
            editItem: { ...this.editItem, name: t('content.copyName', { name: this.entityView.name }) },
          },
        }),
      );
    }
  }

  private toCollectionEditDoneDetail(c: TopicViewModel): CollectionEditDoneDetail {
    return {
      type: 'collections',
      uuid: c.uuid,
      editItem: {
        name: c.name,
        description: c.description ?? '',
        pages: c.pages ? c.pages.map((p) => p + '') : [],
        items: c.items,
      },
    };
  }

  async editCollectionRelation() {
    let recurringEventType = '';
    let occurrenceUuids: string[] = [];
    if (
      (this.entityView.type === 'eventOccurrences' || this.entityView.type === 'meetingOccurrences') &&
      this.entityView['occurrences'] &&
      this.entityView['occurrences'].length > 1
    ) {
      recurringEventType = this.entityView.type;
      occurrenceUuids = this.entityView['occurrences'].map((o) => {
        return o.occurrenceUuid;
      });
    }
    const result: AddToCollectionDialogResult = await AddToCollectionDialog.open({
      entityUuid: this.entityView.uuid,
      entityType: this.entityView.type,
      collections: this.entityView.availableCollections,
      recurringEventType,
      occurrenceUuids,
    });
    if (result.action === 'done') {
      this.dispatchEvent(
        new CustomEvent<{ collections: CollectionEditDoneDetail[]; newCollection?: CollectionEditDoneDetail }>(
          'update-collections',
          {
            bubbles: true,
            composed: true,
            detail: {
              collections: result.collections.map((c) => this.toCollectionEditDoneDetail(c)),
              newCollection: result.newCollection ? this.toCollectionEditDoneDetail(result.newCollection) : undefined,
            },
          },
        ),
      );
    } else if (result.action === 'cancel') {
      return;
    }
  }

  async removeCurrentEntityFromHsePlan() {
    const collection = this.entityView.availableCollections.find((c) => c.template?.id === hsePlanTemplateId);
    if (collection) {
      collection.items = collection.items.filter((i) => i.uuid !== this.entityView.uuid);
      this.dispatchEvent(
        new CustomEvent<{ collections: CollectionEditDoneDetail[] }>('update-collections', {
          bubbles: true,
          composed: true,
          detail: {
            collections: [this.toCollectionEditDoneDetail(collection)],
          },
        }),
      );
    }
  }

  async onDone() {
    if (!this.isDoneDisabled && this.editItem !== undefined) {
      const editItemToSave = await this.saveEditItem(this.editItem);
      if (editItemToSave !== undefined) {
        this.mode = 'saving';
        this.dispatchEvent(
          new CustomEvent('done', {
            bubbles: true,
            composed: true,
            detail: {
              editItem: editItemToSave,
              afterSave: async () => {
                if (this.collectionUuid) {
                  const collection = this.entityView.availableCollections.find((c) => c.uuid === this.collectionUuid);
                  if (collection) {
                    this.dispatchEvent(
                      new CustomEvent<{
                        collectionUuid: string;
                        entityUuid: string;
                        entityType: string;
                      }>('add-to-collection', {
                        bubbles: true,
                        composed: true,
                        detail: {
                          collectionUuid: this.collectionUuid,
                          entityUuid: this.entityView.uuid,
                          entityType: this.entityView.type,
                        },
                      }),
                    );
                  }
                }
                this.editItem = undefined;
                if (this.noView) {
                  this.dispatchEvent(
                    new CustomEvent<{ href: string }>('navigate', {
                      bubbles: true,
                      composed: true,
                      detail: { href: this.entityView.parentHref },
                    }),
                  );
                } else {
                  this.mode = 'normal';
                  this.editItem = undefined;
                  this.dispatchEvent(new CustomEvent('edit-off', { bubbles: true, composed: true }));
                }
              },
            },
          }),
        );
        this.editItem = undefined;
      }
    }
  }

  _resetRevisionsMode() {
    if (this.mode === 'diff') {
      this.mode = 'normal';
    }
  }

  _revisionsAreSame(a, b) {
    return a.name === b.name && a.content === b.content;
  }

  _findSuggested(revisionsInfo) {
    const rev = revisionsInfo.filter(function (i) {
      return i.status === 'SUGGESTED';
    });

    if (rev.length > 0) {
      return rev[0];
    }
  }

  _findCurrent(revisionsInfo) {
    const rev = revisionsInfo.filter(function (i) {
      return i.status === 'CURRENT';
    });

    if (rev.length > 0) {
      return rev[0];
    }
  }

  _sortRevisions(a, b) {
    if (!(a.dateTime && b.dateTime)) {
      return 0;
    }
    if (!b.dateTime) {
      return -1;
    }
    if (!a.dateTime) {
      return 1;
    }
    return b.dateTime.localeCompare(a.dateTime);
  }

  _formatRevisionLabel(date, changedBy, status, singleUserVersion) {
    const formattedDate = LocalDateTime.fromString(date).toStringForDisplay();
    let formattedStatus;
    let changedByText = changedBy;
    if (singleUserVersion && changedBy?.toLowerCase() !== 'trinnvis-teamet') {
      changedByText = t('content.revisionChangedByMe');
    }
    switch (status) {
      case 'CURRENT':
        formattedStatus = t('content.revisionCurrentVersion');
        break;
      case 'ARCHIVED':
        formattedStatus = '';
        break;
      case 'SUGGESTED':
        formattedStatus = t('content.revisionSuggestedUpdate');
        break;
      case 'DELETED':
        formattedStatus = t('content.revisionSuggestedDeletion');
        break;
      default:
        formattedStatus = '';
    }

    return formattedDate + ' ' + changedByText + formattedStatus;
  }

  _revisionTemplateDeleted(status: string) {
    return status === 'SUGGESTED';
  }

  _revisionTemplateUpdated(status) {
    return status === 'SUGGESTED';
  }

  _toDiffMode(e) {
    this.revision = e.detail.clickData;
    this.mode = 'diff';
  }

  async onViewSuggestion() {
    if ('revisions' in this.entityView && !this.entityView.deleted) {
      const r = this.entityView.revisions;
      const s = r.find((x) => x.status === 'SUGGESTED');
      if (s !== undefined) {
        this.revision = s;
        this.mode = 'diff';
      }
    }
  }

  _isCurrent(status: string) {
    return status === 'CURRENT';
  }

  _restore() {
    this.dispatchEvent(
      new CustomEvent<{ entityType: string; entityId: string }>('restore', {
        bubbles: true,
        composed: true,
        detail: {
          entityType: this.entityView.type,
          entityId: this.entityView.uuid,
        },
      }),
    );
  }

  _keep() {
    this._resetRevisionsMode();
  }

  onDelete() {
    if (!this.entityView.deletable) {
      return;
    }
    // TODO: nye dokumenter bør slettes helt, ikke i papirkurven
    if (this.deleteWarning && this.entityView.isConfirmedEntity) {
      if (confirm(this.deleteWarning)) {
        this.dispatchEvent(
          new CustomEvent('entity-delete', {
            bubbles: true,
            composed: true,
            detail: { url: this.afterDeleteHref },
          }),
        );
        this.dispatchEvent(new CustomEvent('edit-off', { bubbles: true, composed: true }));
      }
    } else {
      this.dispatchEvent(
        new CustomEvent('entity-delete', { bubbles: true, composed: true, detail: { url: this.afterDeleteHref } }),
      );
      this.dispatchEvent(new CustomEvent('edit-off', { bubbles: true, composed: true }));
    }
  }

  renderViewMode() {
    return html``;
  }

  abstract initializeEditItem(): Promise<void>;

  renderEditItem(_item: U): TemplateResult<1> {
    return html``;
  }

  renderFieldSection(entityType: string, fields: SectionField[], item: any, themePage: boolean = false) {
    return html`
      <d-field-section
        ?theme-page="${themePage}"
        .entityType=${entityType}
        .item=${item}
        .fields=${fields}
        .contentStickyTop=${this.contentStickyTop}
        @item-changed=${(e) => this.onFieldSectionChanged(e)}
        @field-action=${(e) => this.onFieldAction(e)}
      ></d-field-section>
    `;
  }

  renderEditMode() {
    if (this.editItem === undefined) {
      this.doInitializeEditItem()
        .then(() => console.log('Initialized edit item'))
        .catch((error) => {
          // Error already shown to user in doInitializeEditItem
          console.error('[renderEditMode] Failed to initialize edit item:', error);
          // Force re-render to show normal mode instead of empty screen
          this.requestUpdate();
        });
      return html``;
    }
    return html`${this.renderPreEditMode()} ${this.renderEditItem(this.editItem)}
    ${this.renderEditSelectPages(this.editItem)} `;
  }

  renderCollectionAction() {
    if (this.entityView.featureStates.hse && !this.entityView.featureStates.collections) {
      return html` <d-action @click=${() => this.removeCurrentEntityFromHsePlan()}>${t('content.remove')}</d-action>`;
    }
    return html` <d-action @click=${() => this.editCollectionRelation()}>${t('content.change')}</d-action>`;
  }

  renderCollections() {
    if (this.collectionsWithCurrentEntity.length) {
      return html` <div class="related-entities-section collection-section">
        <div>
          ${this.collectionsWithCurrentEntity.map((collection) => {
            return html`<a href="${collection.href ?? ''}">${collection.name}</a>`;
          })}
        </div>
        ${this.renderCollectionAction()}
      </div>`;
    }
    return html``;
  }

  renderPreContent() {
    return html``;
  }

  renderPreEditMode() {
    return html``;
  }

  renderAfterListsContent() {
    return html``;
  }

  _revisionItems(r) {
    return sorted(r, this._sortRevisions).map((item) => {
      let updateStatus = 'none';
      if (this._revisionTemplateDeleted(item.status)) {
        updateStatus = 'templateDeleted';
      }
      if (this._revisionTemplateUpdated(item.status)) {
        updateStatus = 'templateUpdated';
      }
      const accessible = !this._isCurrent(item.status);
      return {
        label: this._formatRevisionLabel(item.dateTime, item.changedBy, item.status, this.singleUserVersion),
        updateStatus: updateStatus,
        accessible: accessible,
        clickData: item,
      };
    });
  }

  renderRevisionItems(revisions: Revision[]) {
    return html` <d-list-section
      field="generalFields_revisions"
      icon="versions"
      .items=${this._revisionItems(revisions)}
      .contentStickyTop=${this.contentStickyTop}
      @item-clicked=${(e) => this._toDiffMode(e)}
    >
    </d-list-section>`;
  }

  onUploadFile(e: CustomEvent<{ file: File; callback: () => void }>) {
    console.log('onUploadFile');
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent<{ file: File; callback: () => void; type: string; uuid: string }>('upload-file', {
        bubbles: true,
        composed: true,
        detail: {
          ...e.detail,
          type: this.entityView.type,
          uuid: this.entityView.uuid,
        },
      }),
    );
  }

  setContentStickyTop(height: number) {
    this.contentStickyTop = height + this.stickyTop - 2;
  }

  renderMode() {
    switch (this.internalMode) {
      case 'edit':
        return this.renderEditMode();
      case 'normal':
        return this.renderNormalMode();
      case 'diff':
        return this.revision !== undefined
          ? html` <d-entity-diff
              .singleUserVersion=${this.singleUserVersion}
              .revision=${this.revision}
              .templateDeleted=${this.revision.status === 'DELETED'}
              .docsForLinking=${this.entityView.docsForLinking ?? []}
              .contentStickyTop=${this.contentStickyTop}
              @keep=${() => this._keep()}
              @restore-revision=${this.onRestoreRevision}
            ></d-entity-diff>`
          : nothing;
    }
  }

  renderContent() {
    return html`
      <div class="content" id="content">
        ${this.renderHelpSection()} ${this.renderUpdateSection()} ${this.renderStartTaskSection()} ${this.renderMode()}
      </div>
    `;
  }

  render() {
    // TEMPORARY: For PERFORMANCE_REVIEW accounts, always show content even if promoData exists
    // This ensures meeting occurrences (employee interviews) are accessible
    // TODO: Remove this override once the server properly handles PERFORMANCE_REVIEW
    const hasPerformanceReview = this.entityView.featureStates?.performanceReview;

    if (this.entityView.promoData && !hasPerformanceReview) {
      return html`
        ${this.renderHeader()}
        <div class="content" id="content" style="padding-top: 30px">
          <d-promo-section
            .employeesCount=${this.entityView.employeesCount}
            .specialTerms=${this.entityView.specialTerms}
            .promoData=${this.entityView.promoData}
            .features=${features}
            .canUpgrade=${this.entityView.currentUserHasWriteAccess}
          ></d-promo-section>
        </div>
      `;
    }
    return html`
      ${this.renderPdfData()} ${this.renderHeader()}
      ${this.internalMode === 'saving' ? this.renderSaving() : this.renderContent()}
    `;
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    console.log('[EntityContent] disconnectedCallback called for:', this.entityView?.type, this.entityView?.uuid);
    super.disconnectedCallback();
    this.cleanupViewerProperties();
  }

  private cleanupViewerProperties() {
    // Clean up viewer properties resources
    console.log('[EntityContent] cleanupViewerProperties called for key:', this.currentViewerPropertiesKey, {
      hasCleanup: !!this.viewerPropertiesCleanup,
    });

    if (this.viewerPropertiesCleanup) {
      console.log('[EntityContent] Calling cleanup function for:', this.currentViewerPropertiesKey);
      this.viewerPropertiesCleanup();
      this.viewerPropertiesCleanup = undefined;
    } else {
      console.log('[EntityContent] No cleanup function to call for:', this.currentViewerPropertiesKey);
    }

    this.privateNotesText = undefined;
    this.currentViewerPropertiesKey = undefined;
    this.privateNotesLoading = false;
    this.privateNotesInitialized = false;
    this.initializationPromise = undefined;

    console.log('[EntityContent] Cleanup complete');
  }

  onClearDraft() {
    this.dispatchEvent(
      new CustomEvent<{
        entityType: string;
        entityUuid: string;
      }>('clear-draft', {
        composed: true,
        bubbles: true,
        detail: {
          entityType: this.entityView.type,
          entityUuid: this.entityView.uuid,
        },
      }),
    );
    this.onEditModeOff();
  }

  draftHeaderActions() {
    if (
      this.entityView.currentUserHasWriteAccess &&
      (!this.entityView.emergencyContent || this.entityView.currentUserHasEmergencyAccess)
    ) {
      return [
        {
          name: t('content.edit'),
          action: 'edit',
        },
      ];
    }
    return [];
  }

  editHeaderActions() {
    const actions: ActionInput[] = [];
    if (this.internalMode === 'edit') {
      if (this.entityView.hasDraft) {
        actions.push({
          name: t('content.deleteDraft'),
          action: !this.entityView.isConfirmedEntity ? 'delete' : 'clearDraft',
        });
        if (!this.entityView.isConfirmedEntity) {
          actions.push({
            name: t('content.closeDraft'),
            action: 'closeNewDraft',
          });
        } else {
          actions.push({
            name: t('content.closeDraft'),
            action: 'cancel',
          });
        }
      } else if (!this.entityView.isConfirmedEntity) {
        actions.push({
          name: t('content.cancel'),
          action: 'delete',
        });
      } else {
        actions.push({
          name: t('content.cancel'),
          action: 'cancel',
        });
      }
      actions.push({
        name: t('content.done'),
        action: 'done',
        disabled: this.doneDisabled(),
      });
    }
    return actions;
  }

  trashHeaderActions() {
    return [
      {
        name: t('content.restore'),
        action: 'restore',
      },
    ];
  }

  mainHeaderActions() {
    const actions: ActionInput[] = [];
    if (
      !this.entityView.promoData &&
      this.internalMode !== 'saving' &&
      this.internalMode !== 'edit' &&
      !this.entityView.deleted
    ) {
      if (!this.hideShare()) {
        actions.push({
          name: t('content.share'),
          action: 'share',
        });
      }
      if (!this.hideEdit()) {
        actions.push({
          name: t('content.edit'),
          action: 'edit',
        });
      }
    }
    if (this.internalMode !== 'saving' && this.internalMode !== 'edit') {
      actions.push({
        name: t('content.close'),
        href: this.parentHref || '#',
      });
    }
    return actions;
  }

  /**
   * Fetches a fresh authentication key and opens the PDF with it.
   * This prevents PDF generation errors when the key expires after the user
   * has been on the page for an extended time.
   */
  async openPdfWithFreshKey() {
    try {
      console.log('[openPdfWithFreshKey] Fetching fresh key from server...');

      // Get current user
      const store = getStore();
      const state = store.getState();
      const user = state.user;

      if (!user) {
        throw new Error('User not found');
      }

      // Fetch fresh account data with new key
      const api = createClient();
      const account = await api.accounts.listOrganizations({
        accountEmail: user.username,
      });

      const freshKey = account.key;

      if (!freshKey) {
        throw new Error('No key returned from listOrganizations');
      }

      console.log('[openPdfWithFreshKey] Fresh key obtained from server');

      // Parse existing PDF URL
      const pdfUrl = new URL(this.entityView.pdfHref, window.location.origin);

      // Update key parameter
      pdfUrl.searchParams.set('key', freshKey);

      const updatedUrl = pdfUrl.toString();
      console.log('[openPdfWithFreshKey] Updated PDF URL with fresh key');

      // Open PDF based on platform
      await this.openPdfUrl(updatedUrl);

      console.log('[openPdfWithFreshKey] Opened PDF with fresh key');
    } catch (error) {
      console.error('[openPdfWithFreshKey] Failed to fetch fresh key:', error);

      // Fallback: try with original URL (may fail if key is expired)
      console.log('[openPdfWithFreshKey] Falling back to original PDF URL');
      await this.openPdfUrl(this.entityView.pdfHref);
    }
  }

  /**
   * Opens a PDF URL, handling both web and mobile platforms.
   */
  private async openPdfUrl(url: string) {
    if (Capacitor.isNativePlatform()) {
      // Mobile: Open in in-app browser
      await Browser.open({ url });
    } else {
      // Web: Open in new tab
      window.open(url, '_blank');
    }
  }

  async printPdf() {
    const view: ItemPdf = {
      organizationName: this.entityView.organizationName,
      type: this.entityView.type,
      name: this.entityView.name,
      fields: this.pdfFields(),
      typeDisplay: t('content.form'),
    };

    // pdfmake (inkl. innbakte fonter, ~4 MB) lastes ved første bruk (#13442).
    const { createEntityPdf } = await import('../pdf/entity-pdf');
    createEntityPdf(view).download();
  }

  mainHeaderMoreActions() {
    const actions: ActionInput[] = [];
    if (
      this.entityView.featureStates.hseProduct &&
      this.internalMode !== 'saving' &&
      !this.entityView.deleted &&
      this.nonEmptyTranslatableFields.length > 0
    ) {
      actions.push({ name: t('translation.viewInOtherLanguage'), action: 'translate' });
    }
    if (
      !this.entityView.promoData &&
      this.internalMode !== 'saving' &&
      this.internalMode !== 'edit' &&
      !this.entityView.deleted
    ) {
      if (!this.hidePdf()) {
        if (!this.localPdf()) {
          actions.push({
            name: t('content.createPdfOfDocument'),
            action: 'pdf',
          });
        }
        if (this.localPdf()) {
          actions.push({
            name: t('content.createPdfOfDocument'),
            action: 'pdf-print',
          });
        }
      }
      if (!this.hideSaveAsNew()) {
        actions.push(this.saveAsNewAction);
      }
      if (
        this.entityView.featureStates.collections &&
        this.entityView.type !== 'meetings' &&
        this.entityView.type !== 'tutorials' &&
        this.entityView.type !== 'collections' &&
        !this.collectionsWithCurrentEntity.length
      ) {
        actions.push({
          name: t('content.addDocumentToCollection'),
          action: 'addToCollection',
        });
      }
      if (
        this.showPlanReviewAction &&
        this.entityView.featureStates.hseProduct &&
        this.entityView.featureStates.pageEvents &&
        this.entityView.isConfirmedEntity &&
        this.entityView.currentUserHasWriteAccess &&
        !!this.entityView.href
      ) {
        actions.push({
          name: t('review.planAction'),
          action: 'planReview',
        });
      }
      if (
        !this.hideDelete(this.internalMode, this.preventDelete, this.revision) &&
        this.entityView.currentUserHasWriteAccess &&
        (!this.emergencyContent || this.entityView.currentUserHasEmergencyAccess)
      ) {
        actions.push({
          name: t('content.deleteDocument'),
          action: 'delete',
          disabled: !this.entityView.deletable,
          classes: 'delete',
        });
      }
    }
    return actions;
  }

  /**
   * Whether the "Planlegg gjennomgang" (schedule review) action is offered in
   * the more-menu. Available for all entities by default; entity types where a
   * scheduled review makes no sense (e.g. the task/event occurrence views
   * themselves) override this to return false.
   */
  protected get showPlanReviewAction(): boolean {
    return true;
  }

  /**
   * Whether the "Oppgaver" section listing scheduled review tasks for this
   * entity is shown. On by default; overridable per entity type.
   */
  protected get showReviewTasksSection(): boolean {
    return true;
  }

  /**
   * Default date for a scheduled review task. Entities with a "valid to" date
   * (agreements/contracts, documents, constitutional documents) default to one
   * week before expiry; otherwise today is used. Overridable per entity type.
   */
  protected planReviewDefaultDate(): string {
    const validToDate = (this.entityView as unknown as { validToDate?: string }).validToDate;
    if (validToDate) {
      return LocalDate.fromString(validToDate).minusDays(7).toString();
    }
    return LocalDate.now().toString();
  }

  /**
   * Default time of day for a scheduled review task. A concrete time (not
   * «hele dagen») is required for a reminder to fire, so review tasks default
   * to 09:00. Overridable per entity type.
   */
  protected planReviewDefaultTime(): string {
    return '09:00';
  }

  /**
   * Default reminder for a scheduled review task. «Ved start» (AT_START) means
   * the user is reminded on the review date itself. Overridable per entity type.
   */
  protected planReviewDefaultReminder(): ReminderType {
    return 'AT_START';
  }

  private reviewTaskName(): string {
    if (!this.entityView.name) {
      return t('review.taskNameFallback');
    }
    // Refer to the document type, e.g. "Gjennomgang av retningslinje for brannvern".
    const typeKey = 'entityTypeNames.' + this.entityView.type;
    const typeName = t(typeKey);
    if (typeName === typeKey) {
      // No type label available for this entity type → fall back to name only.
      return t('review.taskNameNoType', { name: this.entityView.name });
    }
    return t('review.taskName', { type: typeName, name: this.entityView.name });
  }

  /**
   * Builds the HTML description for a review task. The link back to this entity
   * uses the same internal-link format the rich-text editor produces
   * (data-internal-link-id/doctype + target=_self), so it behaves like a
   * user-inserted document link. The entity uuid embedded in the link is what
   * ties the task to the entity in {@link reviewTasksForEntity}.
   */
  private reviewTaskDescription(): string {
    const label = this.entityView.name || t('entityTypeNamesDetermined.' + this.entityView.type);
    const classification = (this.entityView as unknown as { classification?: string }).classification;
    const classifiedAttr = classification && classification !== 'NONE' ? ' data-internal-link-classified' : '';
    const link =
      '<a href="' +
      escapeHtml(this.entityView.href) +
      '"' +
      classifiedAttr +
      ' data-internal-link-doctype="' +
      escapeHtml(this.entityView.type) +
      '" data-internal-link-id="' +
      escapeHtml(this.entityView.uuid) +
      '" target="_self">' +
      escapeHtml(label) +
      '</a>';
    return '<p>' + t('review.taskDescription', { link }) + '</p>';
  }

  /**
   * Creates a one-off review task (event occurrence) for this entity, prefilled
   * with a default date, name and a link back to the entity in its description,
   * then navigates to it in edit mode so the user can complete the details
   * (time, reminder, assignee). Reuses the standard create-entity flow.
   */
  private planReview() {
    const id = uuid();
    // time/reminder cannot be set on create; they are carried as query params
    // and applied when the new task opens in edit mode (see d-event-occurrence-view).
    // nameConfirmed=true skips the routine/name-selection step since the task
    // already gets a name here.
    const prefill =
      '?edit&nameConfirmed=true&time=' +
      encodeURIComponent(this.planReviewDefaultTime()) +
      '&reminder=' +
      encodeURIComponent(this.planReviewDefaultReminder());
    const input: CreateEventOccurrenceInput = {
      entityType: 'eventOccurrences',
      entityUuid: id,
      instance: this.planReviewDefaultDate(),
      name: this.reviewTaskName(),
      description: this.reviewTaskDescription(),
      targetUrl: this.entityView.href + '/eventOccurrences/' + id + prefill,
    };
    this.dispatchEvent(
      new CustomEvent<CreateEntityInput>('create-entity', {
        bubbles: true,
        composed: true,
        detail: input,
      }),
    );
  }

  /**
   * Open (not yet done) review tasks linked to this entity, matched via the
   * internal link (data-internal-link-doctype/id) embedded in the task
   * description. Read from the store directly so the section is available to
   * every entity type without per-type plumbing.
   */
  private reviewTasksForEntity(): EventOccurrenceViewModel[] {
    if (
      !this.showReviewTasksSection ||
      !this.entityView.featureStates.hseProduct ||
      !this.entityView.href ||
      !this.entityView.isConfirmedEntity
    ) {
      return [];
    }
    const entityUuid = this.entityView.uuid;
    const entityType = this.entityView.type;
    return eventOccurrencesNotDeleted(getStore().getState())
      .filter((e) => !e.doneDate && e.uuid !== entityUuid)
      .filter((e) => descriptionLinksToEntity(e.description, entityType, entityUuid))
      .slice()
      .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
  }

  private renderReviewTasksSection(): TemplateResult<1> | typeof nothing {
    const tasks = this.reviewTasksForEntity();
    if (!tasks.length) {
      return nothing;
    }
    const items: ListSectionItemInput[] = tasks.map((e) => ({
      label: e.name || t('review.taskNameFallback'),
      rightLabel: e.date ? LocalDate.fromString(e.date).toStringForDisplayWithYear() : t('library.todo.soonest'),
      href: this.entityView.href + '/eventOccurrences/' + e.uuid,
      accessible: true,
    }));
    return html`
      <d-list-section
        label=${t('library.taskEvents.tasks')}
        icon="events"
        .items=${items}
        .contentStickyTop=${this.contentStickyTop}
      ></d-list-section>
    `;
  }

  renderHelpSection() {
    return html`
      <d-help-section
        content="${this.entityView.helpContent}"
        help-only="${this.helpOnly}"
        ?opened="${this._helpSectionOpened(
          this.helpOnly,
          this.helpOpened,
          this.mode,
          this.templateId,
          this.entityView.unchangedTemplate,
          this.entityView.isConfirmedEntity,
        )}"
        update-section-opened="${this.updateSectionOpened}"
      ></d-help-section>
    `;
  }

  renderPdfData() {
    const showPdfData = localStorage.getItem('showPdfData');
    if (showPdfData === 'TRUE') {
      return html`<textarea>
        ${getPdfTemplateDefinitionForEntity(this.viewFields(), this.lists(), this.entityView)}
      </textarea
      >`;
    }
    return nothing;
  }

  async repeatRiskAssessment() {
    /**
     * overridden in risk view
     */
  }

  toMyUnion(value: string): 'fullWidth' | 'maxWidth300' {
    const allowedValues: ('fullWidth' | 'maxWidth300')[] = ['fullWidth', 'maxWidth300'];
    return allowedValues.includes(value as 'fullWidth' | 'maxWidth300')
      ? (value as 'fullWidth' | 'maxWidth300')
      : 'fullWidth';
  }

  protected pdfFields(): PdfField[] {
    const n = getPdfTemplateDefinitionForEntity(this.viewFields(), this.lists(), this.entityView);
    alert(n);

    const fields = getViewFieldsForPdfTemplate(this.viewFields(), this.entityView.type);

    const systemFields: SystemField[] = fields
      .filter((f) => {
        return f.type === 'view-text' || f.type === 'view-html';
      })
      .map((f) => {
        switch (f.type) {
          case 'view-text':
            return {
              type: 'text',
              text: getValueForPdfTemplate(this.entityView[f.field ?? ''], f['options']),
              label: f.label,
              width: this.toMyUnion(f.class) ?? undefined,
            };
          case 'view-html':
            return {
              type: 'html',
              htmlContent: getValueForPdfTemplate(this.entityView[f.field ?? ''], f['options']),
              label: f.label,
            };
          default:
            throw new Error('Unsupported field type ' + f.type);
        }
      });
    return mapFieldsToPdfContent(systemFields);
  }

  protected hideDelete(mode: string, preventDelete: boolean, revision) {
    if (mode === 'normal') {
      return preventDelete;
    } else if (mode === 'diff' && revision.status === 'SUGGESTED') {
      return true;
    } else if (mode === 'diff' && revision.status === 'DELETED') {
      return false;
    }
    return true;
  }

  protected hideEdit() {
    return (
      !this.hasEditAccess ||
      (this.emergencyContent && !this.entityView.currentUserHasEmergencyAccess) ||
      this.internalMode !== 'normal' ||
      this.itemLocked ||
      this.eventDone ||
      this.entityView.hasDraft
    );
  }

  protected hidePdf() {
    return this.mode !== 'normal' || this.helpOnly;
  }

  protected localPdf() {
    return false;
  }

  protected doneDisabled(): boolean {
    return false;
  }

  protected onFieldSectionChanged(e) {
    this.editItem = e.detail.value;
  }

  protected onFieldAction(e) {
    this[e.detail.value]();
  }

  protected renderEditClassification(item: any, themePage: boolean = false): TemplateResult<1> {
    if (this.entityView.currentUserIsEmployee) {
      return html`
        <d-section>
          <d-edit-classification
            ?theme-page="${themePage}"
            .accessControl=${item.accessControl}
            .accessControlOptions=${this.entityView.accessControlOptions}
            .classification=${item.classification}
            @value-changed=${(
              e: CustomEvent<{
                accessControl: string[];
                classification: 'NONE' | 'CONFIDENTIAL';
              }>,
            ) => {
              this.editItem = {
                ...item,
                accessControl: e.detail.accessControl,
                classification: e.detail.classification,
              };
            }}
          ></d-edit-classification>
        </d-section>
      `;
    }
    return html``;
  }

  protected renderEditSelectPages(item: any, themePage: boolean = false): TemplateResult<1> {
    if (
      (this.entityView.featureStates.core || this.entityView.featureStates.hse) &&
      this.entityView.availablePages &&
      this.entityView.availablePages.length
    ) {
      const availablePages = this.entityView.availablePages.map((p) => {
        if (p.value === pageIds.emergency + '' && !this.entityView.currentUserHasEmergencyAccess) {
          return { ...p, disabled: true };
        }
        return p;
      });
      return html`
        <d-section>
          <d-select-tag
            ?theme-page="${themePage}"
            label=${t('content.showOnThemePage')}
            not-deselectable
            .options=${availablePages}
            .value=${item.pages}
            @value-changed=${(e: CustomEvent<{ value: string[] }>) => {
              this.editItem = { ...item, pages: e.detail.value };
            }}
          ></d-select-tag>
        </d-section>
      `;
    }
    return html``;
  }

  protected async saveEditItem(item: U): Promise<U | undefined> {
    return item;
  }

  protected renderRevisions(): TemplateResult<1> | typeof nothing {
    return 'revisions' in this.entityView && !this.entityView.deleted
      ? this.renderRevisionItems(this.entityView.revisions)
      : nothing;
  }

  protected onShareContent(_message: string = '', _contacts: EntityOption[] = []) {
    this.dispatchEvent(
      new CustomEvent('share-content', {
        bubbles: true,
        composed: true,
        detail: { message: _message, contacts: _contacts },
      }),
    );
  }

  protected updated(_changedProperties: PropertyValues) {
    if (_changedProperties.has('editItem')) {
      this.debouncedDoBackup();
    }
    if (_changedProperties.has('mode') && this.mode === 'done') {
      this.onDone();
    }
  }

  protected renderPrivateNotes() {
    if (!this.entityView.featureStates.privateNotes) {
      return nothing;
    }

    const config = this.viewerPropertiesConfig();
    if (!config) {
      return nothing;
    }

    // Only show private notes for entity types that support them
    const supportedTypes = ['meetingOccurrences'];
    if (!supportedTypes.includes(this.entityView.type)) {
      return nothing;
    }

    // Check if we need to initialize or reinitialize based on config changes
    const configKey = `${config.organizationId}-${config.entityType}-${config.entityId}`;
    // Check if we need to create a new provider
    if (this.currentViewerPropertiesKey !== configKey) {
      // Cancel any pending initialization
      this.initializationPromise = undefined;
      // Clean up old provider
      if (this.viewerPropertiesCleanup) {
        console.log('[EntityContent] Cleaning up old provider');
        this.cleanupViewerProperties();
      }

      // Set the new key immediately to prevent duplicate initialization
      this.currentViewerPropertiesKey = configKey;
      this.privateNotesLoading = true;
      this.privateNotesInitialized = false;

      // Start initialization if not already in progress
      if (!this.initializationPromise) {
        console.log('[EntityContent] Starting new initialization for:', configKey);
        this.initializationPromise = this.initializeViewerProperties(config);
      }
    } else if (!this.viewerPropertiesCleanup && !this.initializationPromise && !this.privateNotesInitialized) {
      // Provider doesn't exist and we're not initializing and not initialized - start initialization
      console.log('[EntityContent] No provider and no init in progress, starting initialization');
      this.privateNotesLoading = true;
      this.initializationPromise = this.initializeViewerProperties(config);
    }

    // Show loading state only if we're loading AND not initialized yet (prevents flicker)
    if (this.privateNotesLoading && !this.privateNotesInitialized) {
      return html`
        <d-section label=${t('content.myPrivateNotes')}>
          <div style="padding: 8px; color: #666;">${t('content.loadingNotes')}</div>
        </d-section>
      `;
    }

    // Show the textarea if initialized (component is self-contained, no need to re-render)
    const privateNotesText = this.privateNotesText;
    if (this.privateNotesInitialized && privateNotesText) {
      return html`
        <d-section label=${t('content.myPrivateNotes')}>
          <d-yjs-textarea
            .yjsText=${privateNotesText}
            placeholder=${t('content.privateNotesPlaceholder')}
            rows="4"
          ></d-yjs-textarea>
        </d-section>
      `;
    }

    // Default: return nothing while waiting
    return nothing;
  }

  private async initializeViewerProperties(config: ViewerPropertiesConfig) {
    console.log('[EntityContent] Starting initializeViewerProperties for:', config);
    try {
      const { notesText, cleanup } = await viewerPropertiesManager.getProvider(
        config.organizationId,
        config.entityType,
        config.entityId,
        config.userId,
      );

      this.privateNotesText = notesText;
      this.viewerPropertiesCleanup = cleanup;
      this.privateNotesLoading = false;
      this.privateNotesInitialized = true;

      console.log('[EntityContent] Viewer properties initialized:', {
        hasCleanup: !!this.viewerPropertiesCleanup,
        hasText: !!this.privateNotesText,
        textContent: this.privateNotesText?.toString(),
      });

      // Clear the initialization promise
      this.initializationPromise = undefined;

      // Trigger re-render to show the textarea
      this.requestUpdate();
    } catch (error) {
      console.error('[EntityContent] Failed to initialize viewer properties:', error);
      this.privateNotesLoading = false;
      this.privateNotesInitialized = false;
      this.initializationPromise = undefined;
      this.requestUpdate();
    }
  }

  protected renderAttachments() {
    return html` <d-list-section-attachment
      .entityType=${this.entityView.type}
      .items=${this.entityView.attachments}
      .writeAccess=${this.entityView.currentUserHasWriteAccess}
      .emergencyRestrictions=${this.emergencyContent === true && !this.entityView.currentUserHasEmergencyAccess}
      @upload-file=${this.onUploadFile}
      .contentStickyTop=${this.contentStickyTop}
    ></d-list-section-attachment>`;
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    if (_changedProperties.has('editItem')) {
      this.debouncedDoBackup();
    }
  }

  protected viewFields(): SectionField[] {
    return [];
  }

  protected getIcon() {
    return this.entityView.type;
  }

  protected abstract asUpdateSectionItem(): UpdateSectionItem | undefined;

  protected async willUpdate(_changedProperties?: PropertyValues) {
    await this.checkForEditMode();

    // When entity changes, clean up viewer properties before the next render
    if (_changedProperties?.has('entityView')) {
      const oldEntityView = _changedProperties.get('entityView') as any;
      const newEntityView = this.entityView;

      if (oldEntityView && newEntityView) {
        console.log('[EntityContent] Entity changed:', {
          from: `${oldEntityView.type}/${oldEntityView.uuid}`,
          to: `${newEntityView.type}/${newEntityView.uuid}`,
          hasProvider: !!this.viewerPropertiesCleanup,
        });
      }

      if (this.viewerPropertiesCleanup) {
        console.log('[EntityContent] Cleaning up old viewer properties');
        this.cleanupViewerProperties();
      }
    }
  }

  protected handleAction(_action: string) {}

  protected async checkForEditMode() {
    const query = location.search.slice(1);
    const params = new URLSearchParams(query);
    if (params.has('edit') && this.entityView.href === location.pathname) {
      history.replaceState({}, '', location.protocol + '//' + location.host + location.pathname);
      await this.onEditModeOn();
    }
  }

  protected lists(): ListDefinition[] {
    return [];
  }

  private renderList(list: ListDefinition) {
    if (list.type === 'custom' && list.render) {
      return list.render();
    }
    let items: ListSectionItemInput[] = [];
    if (list.items) {
      items = this.entityView[list.items];
    }
    return html`
      <d-list-section
        ?bordered="${list.icon === 'events' || list.field === 'tasks_assets'}"
        .actions=${list.actions ?? []}
        .field=${list.field ?? ''}
        .sublabel=${list.sublabel ?? ''}
        .icon=${list.icon}
        class="${list.class ?? ''}"
        .items=${items}
        .writeAccess=${this.entityView.currentUserHasWriteAccess}
        .contentStickyTop=${this.contentStickyTop}
        .deleteButton=${list.deleteButton ?? false}
        @action=${(e: CustomEvent) => this.onListSectionAction(e.detail)}
        @remove-item=${(e: CustomEvent) => this.onRemoveListItem(e.detail)}
      ></d-list-section>
    `;
  }

  protected renderListSections(): TemplateResult<1> {
    return html`
      ${this.lists().map((list) => {
        if (list.smoothResize) {
          return html`
            <d-smooth-resize class="list-resize-wrapper">
              ${!Object.hasOwn(list, 'condition') || list.condition ? html` ${this.renderList(list)}` : nothing}
            </d-smooth-resize>
          `;
        }
        if (!Object.hasOwn(list, 'condition') || list.condition) {
          return this.renderList(list);
        }
        return html``;
      })}
    `;
  }

  protected onRemoveListItem(detail: ListSectionItemInput) {
    console.log(detail);
  }

  protected async onBackup() {
    return new Promise<void>((resolve) => {
      if (
        this.internalMode === 'edit' &&
        this.editItem &&
        !isEqual(
          {
            editItem: this.editItem,
            entityUuid: this.entityView.uuid,
          },
          this.previousEditItem,
        )
      ) {
        this.previousEditItem = { editItem: this.editItem, entityUuid: this.entityView.uuid };
        this.savingDraft = true;
        setTimeout(() => {
          this.savingDraft = false;
        }, 1000);
        this.dispatchEvent(
          new CustomEvent<{
            entityType: string;
            entityUuid: string;
            draft: Record<string, unknown>;
            done: () => void;
          }>('save-draft', {
            composed: true,
            bubbles: true,
            detail: {
              entityType: this.entityView.type,
              entityUuid: this.entityView.uuid,
              draft: this.editItem,
              done: () => {
                console.log('SAVE DRAFT DONE');
                resolve();
              },
            },
          }),
        );
      } else {
        resolve();
      }
    });
  }

  protected renderUpdateSection() {
    const item = this.asUpdateSectionItem();
    return 'revisions' in this.entityView &&
      !this.entityView.deleted &&
      this._normalMode(this.mode) &&
      item !== undefined
      ? html` <d-update-section
          .helpSectionPresent=${!isEmptyHtmlContent(this.entityView.helpContent)}
          .item=${item}
          .today=${this.entityView.today}
          @view-suggestion=${this.onViewSuggestion}
          @delete=${() => this.onDelete()}
          @decline=${(e) => this.onUpdateDeclined(e)}
        >
        </d-update-section>`
      : nothing;
  }

  protected hideSaveAsNew() {
    return this.mode !== 'normal' || !this.entityView.isConfirmedEntity || !this.duplicatable;
  }

  private hideShare() {
    return this.singleUserVersion || this.mode === 'edit' || this.mode === 'diff';
  }

  private async doInitializeEditItem(): Promise<void> {
    if (this.entityView.hasDraft) {
      try {
        this.editItem = await this.entityView.fetchDraft();
      } catch (error) {
        console.error('[doInitializeEditItem] Failed to fetch draft:', error);
        // Show error to user and prevent edit mode
        alert(t('content.couldNotFetchDraft'));
        throw error; // Re-throw to prevent edit mode from activating
      }
    } else {
      await this.initializeEditItem();
    }
  }

  private renderSaving() {
    return html` <div class="content delayedFadeIn" id="content" style="margin-top: 250px; opacity: 0;">
      <d-spinner label=${t('content.saving')}></d-spinner>
    </div>`;
  }

  private onRestoreRevision(e: CustomEvent<{ name: string; content: string; status: string }>) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent<{ name: string; content: string; status: string; type: string; uuid: string }>(
        'restore-revision',
        {
          bubbles: true,
          composed: true,
          detail: {
            ...e.detail,
            type: this.entityView.type,
            uuid: this.entityView.uuid,
          },
        },
      ),
    );
    this._resetRevisionsMode();
  }

  private onUpdateDeclined(event: CustomEvent) {
    event.stopPropagation();
    this.dispatchEvent(
      new CustomEvent('update-declined', {
        bubbles: true,
        composed: true,
        detail: {
          entityType: this.entityView.type,
          entityId: this.entityView.uuid,
        },
      }),
    );
  }

  private renderNormalMode() {
    if (!this.entityView.currentUserHasAccess) {
      return html``;
    }

    if (this.entityView.deleted) {
      return this.renderViewMode();
    }

    return html`${this.renderCollections()} ${this.renderPreContent()} ${this.renderViewMode()}
    ${this.renderPrivateNotes()} ${this.renderListSections()} ${this.renderReviewTasksSection()}
    ${this.renderAfterListsContent()} ${this.renderRevisions()}`;
  }

  private async handleHeaderAction(e) {
    e.preventDefault();
    await this.debouncedDoBackup.flush();
    switch (e.detail) {
      case 'edit':
        return this.onEditModeOn();
      case 'clearDraft':
        return this.onClearDraft();
      case 'closeNewDraft':
        return this.onEditModeOff();
      case 'cancel':
        return this.onEditModeOff();
      case 'delete':
        return this.onDelete();
      case 'saveAsNew':
        return this._saveAsNew();
      case 'repeatRiskAssessment':
        return this.repeatRiskAssessment();
      case 'addToCollection':
        return this.editCollectionRelation();
      case 'done':
        return this.onDone();
      case 'share':
        return this.onShareContent();
      case 'restore':
        return this._restore();
      case 'pdf':
        return this.openPdfWithFreshKey();
      case 'pdf-print':
        return this.printPdf();
      case 'planReview':
        return this.planReview();
      case 'translate':
        return this.openTranslationDialog();
      default:
        return this.handleAction(e.detail);
    }
  }

  /**
   * Free-text and HTML fields that can be AI-translated for this entity.
   * Norwegian is the authoritative source. Field definitions come from the
   * central registry keyed by entity type; an empty list hides the action.
   */
  protected translatableFields(): TranslatableField[] {
    const defs = getTranslatableFields()[this.entityView.type] ?? [];
    const view = this.entityView as unknown as Record<string, unknown>;
    return defs.map((def) => {
      const value = view[def.key];
      return { ...def, value: typeof value === 'string' ? value : '' };
    });
  }

  private get nonEmptyTranslatableFields(): TranslatableField[] {
    return this.translatableFields().filter((f) =>
      f.type === 'html' ? !isEmptyHtmlContent(f.value) : (f.value ?? '').trim() !== '',
    );
  }

  protected async openTranslationDialog(): Promise<void> {
    if (!this.entityView.featureStates.hseProduct) return;
    const fields = this.nonEmptyTranslatableFields;
    if (fields.length === 0) return;
    const organizationId = String(getOrganizationId(getStore().getState()));
    await TranslationDialog.open({
      organizationId,
      sourceType: this.entityView.type,
      sourceUuid: this.entityView.uuid,
      sourceLabel: this.entityView.name,
      fields,
      canEdit: this.entityView.currentUserHasWriteAccess,
    });
  }

  protected get emergencyContent() {
    return (
      (this.entityView.pages && this.entityView.pages.includes(pageIds.emergency + '')) ||
      this.entityView.emergencyContent === true
    );
  }

  private renderHeader() {
    return html` <d-entity-header
      icon="${this.getIcon()}"
      style="top: ${this.stickyTop - 1}px"
      .label=${this.currentItemLabel}
      .secondaryLabel=${this.currentItemSecondaryLabel}
      .sublabel=${this.currentItemSublabel}
      .specialEditLabel=${this.specialEditLabel}
      .specialEditSublabel=${this.specialEditSublabel}
      ?covered="${this.covered}"
      href="${this.entityView.href}"
      .mode=${this.mode}
      .internalMode=${this.internalMode}
      .hasDraft=${this.entityView.hasDraft ?? false}
      .newItem=${!this.entityView.isConfirmedEntity}
      .currentUserHasWriteAccess=${this.entityView.currentUserHasWriteAccess}
      .deleted=${this.entityView.deleted}
      .draftHeaderActions=${this.draftHeaderActions()}
      .editHeaderActions=${this.editHeaderActions()}
      .trashHeaderActions=${this.trashHeaderActions()}
      .mainHeaderActions=${this.mainHeaderActions()}
      .mainHeaderMoreActions=${this.mainHeaderMoreActions()}
      .savingDraft=${this.savingDraft}
      .editName=${this.editName}
      .emergencyContent=${this.emergencyContent}
      @action=${async (e) => this.handleHeaderAction(e)}
      @height-changed=${(e) => this.setContentStickyTop(e.detail)}
    >
    </d-entity-header>`;
  }

  private renderStartTaskSection() {
    return !this.itemLocked
      ? nothing
      : html`
          <d-section>
            <d-view-info content=${t('content.editNotAvailableDuringCreation')}></d-view-info>
          </d-section>
        `;
  }

  private onListSectionAction(detail: string) {
    this[detail]();
  }
}
